import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { UploadSimple, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

const TARGETS = ["no_rm", "nik", "ihs_patient_id", "nama_lengkap", "tempat_lahir", "tanggal_lahir", "jenis_kelamin", "no_hp", "alamat", "email"];

export default function ImportKlikMedis() {
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const inC = "w-full rounded-lg border border-pink-100 bg-[#FDFBFE] px-2 py-1.5 text-sm";

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setReport(null); setBatch(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/rme/import/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data);
      const auto = {}; data.columns.forEach((c) => { const t = TARGETS.find((t) => t.toLowerCase() === c.toLowerCase().replace(/\s+/g, "_")); if (t) auto[t] = c; });
      setMapping(auto);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const dryRun = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/rme/import/dry-run`, { batch_id: batch.batch_id, mapping }); setReport(data); toast.success(`Dry-run: ${data.valid} valid, ${data.failed} gagal, ${data.duplicate} duplikat`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    if (!window.confirm("Commit data valid ke Master Pasien?")) return;
    setBusy(true);
    try { const { data } = await api.post(`/rme/import/commit`, { batch_id: batch.batch_id }); toast.success(`Commit: ${data.inserted} masuk, ${data.skipped} dilewati`); setBatch({ ...batch, committed: true }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rollback = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/rme/import/rollback`, { batch_id: batch.batch_id });
      if (data.kept) toast.warning(`Rollback: ${data.deleted} dihapus · ${data.kept} dipertahankan (sudah digunakan)`);
      else toast.success(`Rollback: ${data.deleted} dihapus`);
      setBatch(null); setReport(null); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-klikmedis">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><UploadSimple size={24} weight="duotone" className="text-[#DB2777]" /> Impor Data KlikMedis</h1>
      <p className="-mt-3 text-sm text-slate-500">Unggah CSV/XLSX → petakan kolom → <b>dry-run</b> → konfirmasi commit. Data ber-IHS otomatis ditandai <i>do_not_resend_satusehat</i>. Tidak pernah dikirim ke SATUSEHAT.</p>

      <label data-testid="import-upload" className="flex w-fit cursor-pointer items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white">
        <UploadSimple size={16} weight="bold" /> Pilih File (.csv / .xlsx)
        <input type="file" accept=".csv,.xlsx" className="hidden" onChange={upload} disabled={busy} />
      </label>

      {batch && (
        <div className="rounded-2xl border border-pink-100 bg-white p-4">
          <div className="mb-2 text-sm font-bold text-slate-700">{batch.filename} · {batch.row_count} baris</div>
          <div className="mb-4 flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase text-slate-500">Pemetaan Kolom (manual)</span>
            <button data-testid="import-preset-klikmedis" type="button" onClick={() => {
              const P = { no_rm: "Nomor RM", nik: "Nomor KTP", nama_lengkap: "Nama Pasien", no_hp: "Nomor HP", jenis_kelamin: "Jenis Kelamin", tempat_lahir: "Tempat Lahir", tanggal_lahir: "Tanggal Lahir", alamat: "Alamat", email: "Email" };
              const m = {}; Object.entries(P).forEach(([t, c]) => { if (batch.columns.includes(c)) m[t] = c; }); setMapping(m);
            }} className="rounded-lg bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">Terapkan Preset KlikMedis</button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {TARGETS.map((t) => (
              <label key={t} className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <span className="w-32 shrink-0">{t}{t === "nama_lengkap" ? "*" : ""}</span>
                <select data-testid={`map-${t}`} className={inC} value={mapping[t] || ""} onChange={(e) => setMapping({ ...mapping, [t]: e.target.value })}>
                  <option value="">—</option>
                  {batch.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            ))}
          </div>
          <button data-testid="import-dryrun-btn" onClick={dryRun} disabled={busy} className="mt-4 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">Jalankan Dry-Run</button>
        </div>
      )}

      {report && (
        <div className="rounded-2xl border border-pink-100 bg-white p-4" data-testid="import-report">
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="rounded-lg bg-emerald-100 px-3 py-1 font-bold text-emerald-700">Valid: {report.valid}</span>
            <span className="rounded-lg bg-rose-100 px-3 py-1 font-bold text-rose-700">Tidak valid: {report.invalid ?? report.failed}</span>
            <span className="rounded-lg bg-amber-100 px-3 py-1 font-bold text-amber-700">Duplikat: {report.duplicate}</span>
            <span className="rounded-lg bg-orange-100 px-3 py-1 font-bold text-orange-700">Perlu Tinjau: {report.review || 0}</span>
            <span className="rounded-lg bg-slate-100 px-3 py-1 font-bold text-slate-600">Total: {report.total}</span>
          </div>
          <div className="mt-3 max-h-64 overflow-auto rounded-xl border border-pink-50">
            <table className="w-full min-w-[520px] text-xs">
              <thead className="bg-[#FAF7FF] text-left text-slate-500"><tr><th className="p-2">#</th><th className="p-2">Nama</th><th className="p-2">No.RM</th><th className="p-2">NIK</th><th className="p-2">Status</th><th className="p-2">Keterangan</th></tr></thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.row} className="border-t border-pink-50">
                    <td className="p-2">{r.row}</td><td className="p-2">{r.nama_lengkap || "-"}</td><td className="p-2 font-mono">{r.no_rm || "-"}</td><td className="p-2 font-mono">{r.nik_masked}</td>
                    <td className="p-2"><span className={`rounded px-1.5 py-0.5 font-bold ${r.status === "valid" ? "bg-emerald-100 text-emerald-700" : r.status === "duplicate" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{r.status}</span></td>
                    <td className="p-2 text-slate-500">{r.reason || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex gap-3">
            <button data-testid="import-commit-btn" onClick={commit} disabled={busy || report.valid === 0} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"><CheckCircle size={16} weight="fill" /> Commit ({report.valid})</button>
            <button data-testid="import-rollback-btn" onClick={rollback} disabled={busy} className="flex items-center gap-1.5 rounded-xl border border-rose-200 px-4 py-2 text-sm font-bold text-rose-600"><ArrowCounterClockwise size={16} /> Rollback Batch</button>
          </div>
        </div>
      )}
    </div>
  );
}
